import { reportDefinitionsApi, type ReportDefinitionChange, type ReportDefinitionSummary } from './api';
import type { ReportDefinitionsBoard } from './report-definitions-board.svelte';
import { ReportLineTenant } from './report-line-tenant';
import type { ReportLineDraft } from './report-line-draft.svelte';
import type { ReportLineWriter } from './report-line-writer';

/**
 * O que se muda numa linha de cliente que já existe: salvar horário e contatos, pausar, ligar e tirar do
 * relatório. Tirar deixa 10 s para desfazer, que recria a linha com o mesmo horário, os mesmos contatos e
 * pausada se estava pausada.
 */
export class ReportDefinitionEditor implements ReportLineWriter {
  busy = $state(false);
  changes = $state<ReportDefinitionChange[]>([]);

  constructor(
    private readonly board: ReportDefinitionsBoard,
    private readonly definition: () => ReportDefinitionSummary,
  ) {}

  async loadChanges(): Promise<void> {
    try {
      this.changes = await reportDefinitionsApi.changes(this.definition());
    } catch (err) {
      this.board.fail(err);
    }
  }

  async save(draft: ReportLineDraft): Promise<boolean> {
    const body = { schedule: draft.schedule.toSchedule(), recipients: draft.toRecipients() };
    return this.write(() => reportDefinitionsApi.update(this.definition(), body));
  }

  async pause(): Promise<void> {
    await this.write(() => reportDefinitionsApi.pause(this.definition()));
  }

  async resume(): Promise<void> {
    await this.write(() => reportDefinitionsApi.resume(this.definition()));
  }

  /** Devolve se tirou, para a gaveta saber se fecha. */
  async remove(): Promise<boolean> {
    const leaving = this.definition();
    this.busy = true;
    try {
      await reportDefinitionsApi.remove(leaving);
      await this.board.load();
      this.board.undo.offer({
        label: `${ReportLineTenant.of(leaving).name} saiu do relatório`,
        revert: () => this.recreate(leaving),
      });
      return true;
    } catch (err) {
      this.board.fail(err);
      return false;
    } finally {
      this.busy = false;
    }
  }

  private async recreate(leaving: ReportDefinitionSummary): Promise<void> {
    try {
      const recreated = await reportDefinitionsApi.create(leaving.tenantId, {
        reportDefinitionGroupId: leaving.reportDefinitionGroupId,
        schedule: leaving.schedule,
        recipients: leaving.recipients.map(({ hqCustomerId }) => ({ hqCustomerId })),
      });
      if (leaving.status === 'paused') await reportDefinitionsApi.pause({ ...leaving, id: recreated.id });
      await this.board.load();
    } catch (err) {
      this.board.fail(err);
    }
  }

  private async write(change: () => Promise<unknown>): Promise<boolean> {
    this.busy = true;
    try {
      await change();
      await Promise.all([this.board.load(), this.loadChanges()]);
      return true;
    } catch (err) {
      this.board.fail(err);
      return false;
    } finally {
      this.busy = false;
    }
  }
}
